import Big from 'big.js';
import { z } from 'zod';

import { DOMAIN } from 'data/constants';

import { DepositCredentials, vndBankSwiftCodes } from './models';

/**
 * The payment page of a deposit as data (payments `GET
 * /public/payments/:id/page`, plan section 9): blocks drawn in order, each
 * when its condition matches and its data is there; deeplinks; recipient
 * bank codes; settings by payer bank; the guides of the instruction kits.
 */

export const LocalizedText = z.object({
  ru: z.string(),
  en: z.string().optional(),
  pt: z.string().optional(),
  hi: z.string().optional(),
  bn: z.string().optional(),
  ta: z.string().optional(),
});
export type LocalizedText = z.infer<typeof LocalizedText>;

export const CopyField = z.enum([
  'amount',
  'baseAmount',
  'requisite',
  'tel',
  'card',
  'account',
  'name',
  'bank',
  'operator',
  'swift',
  'qr',
]);
export type CopyField = z.infer<typeof CopyField>;

const Device = z.enum(['android', 'ios', 'desktop']);
export type PageDevice = z.infer<typeof Device>;
const TransferType = z.enum(['CARD', 'SBP', 'QR']);

export const Condition = z
  .object({
    banks: z.array(z.string()),
    os: z.array(Device),
    viewport: z.enum(['mobile', 'desktop']),
    transferTypes: z.array(TransferType),
    has: z.array(CopyField),
    lacks: z.array(CopyField),
  })
  .partial();
export type Condition = z.infer<typeof Condition>;

export const PageStep = z.object({
  title: LocalizedText,
  titleByOs: z
    .object({
      android: LocalizedText,
      ios: LocalizedText,
      desktop: LocalizedText,
    })
    .partial()
    .optional(),
  description: LocalizedText.optional(),
  copy: z.array(CopyField),
  action: z.enum(['saveQr', 'bankPay']).optional(),
  bankImage: z.enum(['fileHint', 'errorImage']).optional(),
  image: z.string().optional(),
  when: Condition.optional(),
});
export type PageStep = z.infer<typeof PageStep>;

const base = {
  when: Condition.optional(),
  column: z.enum(['left', 'right']).optional(),
};

export const PageBlock = z.discriminatedUnion('type', [
  z.object({ ...base, type: z.literal('title'), text: LocalizedText }),
  z.object({ ...base, type: z.literal('bankSelect') }),
  z.object({ ...base, type: z.literal('amount') }),
  z.object({ ...base, type: z.literal('bankLogo') }),
  z.object({
    ...base,
    type: z.literal('field'),
    field: CopyField,
    label: LocalizedText.optional(),
    truncate: z.boolean().optional(),
    mobileCopyStrip: z.string().optional(),
  }),
  z.object({
    ...base,
    type: z.literal('qr'),
    source: z.enum([
      'requisite',
      'page',
      'deeplinkPage',
      'upiLink',
      'firstDeeplink',
    ]),
    download: z.boolean(),
    onTemplate: z.boolean().optional(),
    caption: LocalizedText.optional(),
  }),
  z.object({
    ...base,
    type: z.literal('deeplinks'),
    style: z.enum(['buttons', 'upiApps', 'elqrApps']),
  }),
  z.object({
    ...base,
    type: z.literal('link'),
    url: z.string(),
    label: LocalizedText,
    once: z.boolean().optional(),
  }),
  z.object({
    ...base,
    type: z.literal('banner'),
    on: z.enum(['always', 'qrFailed']),
    image: z.string().optional(),
    bankErrorImage: z.boolean().optional(),
    title: LocalizedText.optional(),
    text: LocalizedText,
    textMobile: LocalizedText.optional(),
    textMobileDeeplink: LocalizedText.optional(),
  }),
  z.object({
    ...base,
    type: z.literal('notice'),
    icon: z.enum(['refresh', 'warning', 'transfer']).optional(),
    title: LocalizedText.optional(),
    text: LocalizedText,
  }),
  z.object({ ...base, type: z.literal('instructions'), kit: z.string() }),
  z.object({
    ...base,
    type: z.literal('steps'),
    title: LocalizedText.optional(),
    steps: z.array(PageStep),
  }),
]);
export type PageBlock = z.infer<typeof PageBlock>;

export const PageDeeplink = z.object({
  os: z.enum(['android', 'ios']),
  app: z.string(),
  transferType: TransferType,
  templates: z.array(z.string()),
  display: z.enum(['button', 'qr']),
  phoneFormat: z.enum(['encoded', 'digits', 'plus_digits']).optional(),
  amountFormat: z.enum(['decimal', 'whole']).optional(),
});
export type PageDeeplink = z.infer<typeof PageDeeplink>;

export const BankSettings = z.object({
  /** `{{payerBank}}` of the texts: the bank as the client knows it. */
  name: LocalizedText.optional(),
  payImage: z.string().optional(),
  payLink: z.string().optional(),
  buttonName: LocalizedText.optional(),
  fileHint: z.string().optional(),
  qrTemplate: z.string().optional(),
  errorImage: z.string().optional(),
  simple: z.boolean().optional(),
  darkQr: z.boolean().optional(),
});
export type BankSettings = z.infer<typeof BankSettings>;

export const PageData = z.object({
  columns: z.boolean(),
  layout: z.array(PageBlock),
  deeplinks: z.array(PageDeeplink),
  bankCodes: z.record(
    z.string(),
    z.object({
      bankId: z.string().optional(),
      provider: z.string().optional(),
    }),
  ),
  bankSettings: z.record(z.string(), BankSettings),
});
export type PageData = z.infer<typeof PageData>;

export const PageCallout = z.object({
  id: z.string(),
  x: z.number(),
  y: z.number(),
  xMobile: z.number().optional(),
  yMobile: z.number().optional(),
  markerPosition: z.enum(['auto', 'top', 'bottom']).optional(),
  title: LocalizedText.optional(),
  description: LocalizedText,
  copy: z.array(CopyField),
});

export const PageGuide = z.object({
  variant: z.enum(['sbp', 'card']).optional(),
  bank: z.string().optional(),
  device: Device,
  viewport: z.enum(['mobile', 'desktop']).optional(),
  steps: z.array(
    z.object({
      imageKey: z.string(),
      title: LocalizedText.optional(),
      description: LocalizedText.optional(),
      callouts: z.array(PageCallout),
    }),
  ),
});
export type PageGuide = z.infer<typeof PageGuide>;

/** `page: null` — the payment page draws the method with the old code. */
export const DepositPaymentPage = z.object({
  page: PageData.nullable(),
  kits: z.record(z.string(), z.array(PageGuide)),
  countryCode: z.string().nullable(),
});
export type DepositPaymentPage = z.infer<typeof DepositPaymentPage>;

/** The new payment page is opt-in for now: `?renderer=page`. */
export const isPageRendererRequested = () =>
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('renderer') === 'page';

// --- texts and values ---------------------------------------------------------

/** The text in the page language, falling back to `en`, then `ru`. */
export const localized = (
  text: LocalizedText | undefined,
  language: string,
) => {
  if (!text) return '';
  const short = language.slice(0, 2) as keyof LocalizedText;
  return text[short] || text.en || text.ru;
};

/** Values of the deposit by field, for fields, copies and `{{placeholders}}`. */
export type PageValues = Partial<
  Record<CopyField | 'country' | 'payerBank' | 'buttonName', string>
>;

export const depositValues = ({
  amount,
  currency,
  crossborderAmount,
  crossborderCurrency,
  credentials,
  fromBank,
  countryCode,
}: {
  amount: string;
  currency: string;
  crossborderAmount: string | null;
  crossborderCurrency: string | null;
  credentials: DepositCredentials;
  fromBank: string | null;
  countryCode: string | null;
}): PageValues => {
  const value = credentials.value ?? undefined;
  const compact = (values: PageValues) =>
    Object.fromEntries(
      Object.entries(values).filter(([, item]) => item),
    ) as PageValues;

  return compact({
    amount:
      crossborderAmount && crossborderCurrency
        ? `${crossborderAmount} ${crossborderCurrency}`
        : `${amount} ${currency}`,
    baseAmount: `${amount} ${currency}`,
    requisite: value,
    tel:
      credentials.phoneNumber ??
      (credentials.type === 'SBP' ? value : undefined),
    card:
      credentials.cardNumber ??
      (credentials.type === 'CARD' ? value : undefined),
    account: credentials.accountNumber ?? undefined,
    name: credentials.name,
    bank: credentials.bank,
    operator: credentials.bank,
    swift: vndBankSwiftCodes[credentials.bank] ?? credentials.bank,
    qr: credentials.type === 'QR' ? value : undefined,
    country: countryCode ?? undefined,
    payerBank: fromBank ?? undefined,
  });
};

export const fillPlaceholders = (text: string, values: PageValues) =>
  text.replace(
    /\{\{(\w+)\}\}/g,
    (match, key: string) => values[key as keyof PageValues] ?? '',
  );

/**
 * The address of an image key on this payment domain: keys of the payment
 * app build are paths under `/assets`, uploads are `assets/…`.
 */
export const pageImageUrl = (key: string) => {
  if (/^https?:/.test(key)) return key;
  const origin = `https://payment.${DOMAIN}`;
  return key.startsWith('assets/')
    ? `${origin}/${key}`
    : `${origin}/assets/${key}`;
};

// --- conditions ---------------------------------------------------------------

export type PageContext = {
  /** Payer bank of the deposit. */
  bank: string | null;
  os: PageDevice;
  isMobile: boolean;
  transferType: 'CARD' | 'SBP' | 'QR';
  values: PageValues;
};

export const matches = (
  condition: Condition | undefined,
  context: PageContext,
) => {
  if (!condition) return true;
  return (
    (!condition.banks ||
      (!!context.bank && condition.banks.includes(context.bank))) &&
    (!condition.os || condition.os.includes(context.os)) &&
    (!condition.viewport ||
      condition.viewport === (context.isMobile ? 'mobile' : 'desktop')) &&
    (!condition.transferTypes ||
      condition.transferTypes.includes(context.transferType)) &&
    (!condition.has || condition.has.every((field) => context.values[field])) &&
    (!condition.lacks ||
      condition.lacks.every((field) => !context.values[field]))
  );
};

// --- deeplinks ------------------------------------------------------------------

const formatPhone = (format: PageDeeplink['phoneFormat'], phone: string) => {
  if (format === 'plus_digits') return `+${phone.replaceAll(/\D/g, '')}`;
  if (format === 'digits') return phone.replaceAll(/\D/g, '');
  return encodeURIComponent(phone.replaceAll(/[^+\d]/g, ''));
};

const formatAmount = (format: PageDeeplink['amountFormat'], amount: string) => {
  try {
    return new Big(amount).toFixed(format === 'whole' ? 0 : 2);
  } catch {
    return amount;
  }
};

/** The links of a deeplink row for the deposit, tried in order. */
export const deeplinkUrls = (
  row: PageDeeplink,
  page: PageData,
  {
    amount,
    credentials,
    countryCode,
  }: {
    amount: string;
    credentials: DepositCredentials;
    countryCode: string | null;
  },
) => {
  const value = credentials.value ?? '';
  const code = page.bankCodes[credentials.bank] ?? {};
  const afterMark = (mark: string) =>
    value.includes(mark) ? value.slice(value.indexOf(mark) + mark.length) : '';

  return row.templates.map((template) =>
    template
      .replace('{amount}', formatAmount(row.amountFormat, amount))
      .replace('{phoneNumber}', formatPhone(row.phoneFormat, value))
      .replace('{cardNumber}', value.replaceAll(/[^+\d]/g, ''))
      .replace('{qrcode}', value)
      .replace('{country}', countryCode ?? '')
      .replace('{bankId}', code.bankId ?? '')
      .replace('{provider}', code.provider ?? '')
      .replace('{upiParams}', afterMark('upi://pay?'))
      .replace('{qrPayload}', afterMark('#')),
  );
};

/** Rows of a deeplinks block: the payer bank's buttons, or the app lists. */
export const deeplinkRows = (
  page: PageData,
  style: 'buttons' | 'upiApps' | 'elqrApps',
  { os, bank, transferType }: PageContext,
) =>
  os === 'desktop'
    ? []
    : page.deeplinks.filter(
        (row) =>
          row.os === os &&
          row.transferType === transferType &&
          (style === 'buttons' ? row.app === bank : row.app !== bank),
      );
